import { useState } from "react";
import { Clock, MessageSquare } from "lucide-react";
import ConnectionChatInterface from "../../components/knowledge/ConnectionChatInterface";
import { useQuery } from "@tanstack/react-query";
import { getQAHistory } from "../../api/knowledge";
import type { QASession } from "../../types/knowledge";

export default function KnowledgePage() {
  const [activeTab, setActiveTab] = useState<"chat" | "history">("chat");

  const { data: history } = useQuery({
    queryKey: ["qa-history"],
    queryFn: () => getQAHistory(20, 0),
    enabled: activeTab === "history",
  });

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-800">Knowledge Base AI</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Ajukan pertanyaan untuk memperoleh informasi berdasarkan dokumen PNBP
          yang tersedia
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab("chat")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${
            activeTab === "chat"
              ? "bg-white text-blue-700 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <MessageSquare size={14} />
          Chat
        </button>
        <button
          onClick={() => setActiveTab("history")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${
            activeTab === "history"
              ? "bg-white text-blue-700 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <Clock size={14} />
          Riwayat
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0">
        {activeTab === "chat" && (
          <div className="h-full" style={{ maxHeight: "calc(100vh - 200px)" }}>
            <ConnectionChatInterface />
          </div>
        )}

        {activeTab === "history" && (
          <div className="space-y-3 overflow-y-auto h-full">
            {!history?.length && (
              <div className="text-center text-gray-400 mt-12">
                <Clock size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">Belum ada riwayat percakapan</p>
              </div>
            )}
            {history?.map((session: QASession) => (
              <div
                key={session.id}
                className="bg-white border border-gray-200 rounded-xl p-4"
              >
                <div className="flex justify-between items-start mb-2">
                  <p className="text-sm font-medium text-gray-800 line-clamp-1">
                    {session.question}
                  </p>
                  <span className="text-xs text-gray-400 ml-2 flex-shrink-0">
                    {new Date(session.created_at).toLocaleDateString("id-ID")}
                  </span>
                </div>
                {session.answer && (
                  <p className="text-xs text-gray-500 line-clamp-2">
                    {session.answer}
                  </p>
                )}
                <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
                  <span>{session.sources.length} sumber</span>
                  <span>{(session.response_time_ms / 1000).toFixed(1)}s</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
